import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import BackBar from "../components/BackBar";
import { Link } from "react-router-dom";
import { IMG } from "../lib/api";
import { Cross, Heart, Shield } from "lucide-react";
import Brand from "../components/Brand";

export default function FounderStory({ embedded = false }) {
  return (
    <div className="min-h-screen bg-ivory">
      {!embedded && <SiteNav />}
      {!embedded && <BackBar />}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-16">
        <div className="text-xs tracking-wider-cap text-gold-deep text-center mb-3">Mike's Story</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-red-wine text-center">Welcome.</h1>
        <div className="divider-gold"></div>

        {/* JOIN US banner removed Feb 2026 — Mike's call:
            "FAITHARAMA.COM NEEDS TO BE TAKEN OFF OF THE CROSS, THIS AIN'T COOL".
            The brand badge sat directly on the horizontal beam of the cross.
            Pulling the image until a replacement is provided with no text on the cross. */}

        <div className="mt-10 space-y-5 text-lg text-foreground leading-relaxed max-w-3xl">
          <p>"I'm Mike. I'm 68 years old, ordained — a retired Navy instructor, and my ministry is done online."</p>
          <p>"Jesus told us to <em className="text-red-wine font-semibold">'Go into all the world and preach the gospel'</em>. For me, that world reaches right from my keyboard into every living room, kitchen table and homeschool classroom that needs Him."</p>
          <p>"I built <strong><Brand suffix=".com"/></strong> so other believers — pastors, missionaries, homeschool moms, wellness coaches, and regular saints — could have every tool they need under one roof, built around Scripture and the body as a temple."</p>
          <p>"It's Christian all the way. Faith and wellness, together, for the glory of God."</p>
        </div>

        {/* ====== ETERNAL LIFE — PRAYER / SALVATION BOX ======
            Mike's verbatim wording (May 2026). Sits next to the smaller
            church image visually (full-width below) so it carries the
            same intent: "the church reaches you wherever you are." */}
        <div
          data-testid="founder-eternal-life-box"
          className="mt-10 rounded-2xl overflow-hidden shadow-2xl border-4"
          style={{ borderColor: "#F39A1F", background: "linear-gradient(135deg, #BAE6FD 0%, #E0F2FE 100%)" }}
        >
          <div className="p-6 sm:p-9 text-center">
            <Cross className="w-9 h-9 mx-auto" style={{ color: "#F39A1F" }}/>
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold mt-3" style={{ color: "#C9202E" }}>
              ★ A QUESTION WORTH ASKING ★
            </div>
            <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl uppercase leading-[1.15]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, textWrap: "balance", color: "#0F172A" }}>
              Do you know for certain that you have eternal life and that you will go to Heaven when you die?
            </h2>
            <p className="italic mt-4 text-base sm:text-lg" style={{ textWrap: "balance", color: "#0F172A" }}>
              God wants you to be sure.
            </p>

            {/* 1 John 5:13 */}
            <div className="mt-5 mx-auto max-w-3xl rounded-xl p-5 sm:p-6 text-left"
                 style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(243,154,31,0.6)" }}>
              <p className="text-base sm:text-lg italic leading-relaxed" style={{ textWrap: "balance", color: "#0F172A" }}>
                The Bible says, "I write these things to you who believe in the name of the Son of God so that you may know that you have eternal life."
              </p>
              <p className="mt-2 font-bold tracking-wider-cap text-xs" style={{ color: "#C9202E" }}>— 1 John 5:13</p>
            </div>

            <p className="mt-7 text-base sm:text-lg max-w-3xl mx-auto" style={{ textWrap: "balance", color: "#0F172A" }}>
              Another question to consider is: <em>suppose you were standing before God right now and He asked you, <strong>"Why should I let you into My Heaven?"</strong> What do you think you would say?</em>
            </p>
            <p className="mt-3 max-w-3xl mx-auto text-base" style={{ textWrap: "balance", color: "#0F172A" }}>
              You may not know what you would say. But… <strong style={{ color: "#C9202E" }}>you can know</strong>, because God loves you and has a purpose for your life.
            </p>

            {/* John 3:16 */}
            <div className="mt-6 mx-auto max-w-3xl rounded-xl p-5 sm:p-6 text-left"
                 style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(46,159,58,0.6)" }}>
              <p className="text-base sm:text-lg italic leading-relaxed" style={{ textWrap: "balance", color: "#0F172A" }}>
                "For God so loved the world that He gave His one and only Son, that whoever believes in Him shall not perish but have eternal life."
              </p>
              <p className="mt-2 font-bold tracking-wider-cap text-xs" style={{ color: "#2E9F3A" }}>— John 3:16</p>
            </div>

            <p className="italic mt-7 text-base max-w-2xl mx-auto" style={{ textWrap: "balance", color: "#0F172A" }}>
              If you'd like to pray with someone, or just need a listening ear — drop us a note. We answer every message.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-14 stagger">
          <div className="faith-card p-8"><Cross className="w-8 h-8 text-gold-deep mb-3"/><div className="font-serif-display text-2xl text-red-wine">Scripture First</div><p className="text-foreground mt-2">Every product, every email, every dollar — defended from the Bible or it doesn't ship.</p></div>
          <div className="faith-card p-8"><Shield className="w-8 h-8 text-gold-deep mb-3"/><div className="font-serif-display text-2xl text-red-wine">Senior-Friendly</div><p className="text-foreground mt-2">No buzzwords. Big text. Plain English. If a senior can&apos;t run it first try, we rebuild it.</p></div>
          <div className="faith-card p-8"><Heart className="w-8 h-8 text-gold-deep mb-3"/><div className="font-serif-display text-2xl text-red-wine">Faith &amp; Wellness</div><p className="text-foreground mt-2">Your body is a temple worth caring for. Real food, real rest, real prayer.</p></div>
        </div>

        {/* Cross + scripture */}
        <div className="grid md:grid-cols-2 gap-10 mt-16 items-center">
          <img src={IMG.dramaticCross} className="rounded-2xl shadow-xl w-full object-cover aspect-square" alt="The Cross of Christ" />
          <div>
            <div className="text-xs tracking-wider-cap text-gold-deep mb-3">The One we serve</div>
            <h2 className="font-serif-display text-4xl text-red-wine">"I am the way, and the truth, and the life."</h2>
            <p className="mt-4 text-lg text-foreground italic">— John 14:6</p>
            <p className="mt-6 text-lg text-foreground">Every course, every funnel, every email, every testimony on <Brand/> points back to Him. That's the whole point.</p>
          </div>
        </div>

        {/* Creation + hummingbird */}
        <div className="grid md:grid-cols-2 gap-10 mt-16 items-center">
          <div className="order-2 md:order-1">
            <div className="text-xs tracking-wider-cap text-gold-deep mb-3">Consider the birds</div>
            <h2 className="font-serif-display text-4xl text-red-wine">"Look at the birds of the air."</h2>
            <p className="mt-4 text-lg text-foreground italic">— Matthew 6:26</p>
            <p className="mt-6 text-lg text-foreground">If our Father watches over every hummingbird, how much more does He care for you? Rest tonight. Tomorrow we build more of His Kingdom together.</p>
          </div>
          <img src={IMG.hummingbird} className="order-1 md:order-2 rounded-2xl shadow-xl w-full object-cover aspect-square" alt="Hummingbird in flight — consider the birds of the air" />
        </div>

        <div className="text-center mt-14">
          <Link to="/signup" className="btn-primary">Join the Mission — Get All Access</Link>
        </div>
      </section>
      {!embedded && <SiteFooter />}
    </div>
  );
}
